/**
 * Diálogo de ação ergonômica: descrição, se já foi realizada, prazo e responsável (opcionais). Grava na hora.
 */
import React, { useState } from 'react';
import { Checkbox, FormControlLabel, Grid, TextField } from '@mui/material';
import { ergonomicWorkstationService } from '../../../../../services/ergonomicWorkstationService';
import type { ErgonomicAction } from '../../../../../types/ergonomicWorkstation';
import { dialogFieldSx } from './dialogHelpers';
import { ItemDialog } from './ItemDialog';
import { useItemSubmit } from './useItemSubmit';

type ActionField = 'description' | 'dueDate' | 'responsible';

const SERVER_LABELS: ReadonlyArray<[string, ActionField]> = [
  ['Descrição', 'description'],
  ['Prazo', 'dueDate'],
  ['Responsável', 'responsible'],
];

interface ActionDialogProps {
  workstationId: number;
  item: ErgonomicAction | null;
  onSaved: (message: string) => void;
  onClose: () => void;
}

export const ActionDialog: React.FC<ActionDialogProps> = ({ workstationId, item, onSaved, onClose }) => {
  const [description, setDescription] = useState(item?.description ?? '');
  const [done, setDone] = useState(item?.done ?? false);
  const [dueDate, setDueDate] = useState(item?.dueDate ? item.dueDate.slice(0, 10) : '');
  const [responsible, setResponsible] = useState(item?.responsible ?? '');
  const { submitting, error, errors, setErrors, run } = useItemSubmit<ActionField>(
    SERVER_LABELS,
    'Não foi possível guardar a ação. Tente de novo em alguns minutos.'
  );

  const submit = async () => {
    const found: Partial<Record<ActionField, string>> = {};
    if (description.trim() === '') found.description = 'Descreva a ação a ser adotada.';
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const saved = await run(async () => {
      const payload = {
        description: description.trim(),
        done,
        dueDate: dueDate === '' ? null : dueDate,
        responsible: responsible.trim() === '' ? null : responsible.trim(),
      };
      if (item) await ergonomicWorkstationService.updateAction(workstationId, item.id, payload);
      else await ergonomicWorkstationService.createAction(workstationId, payload);
    });
    if (saved) onSaved(item ? 'Ação atualizada.' : 'Ação guardada.');
  };

  return (
    <ItemDialog
      title={item ? 'Editar ação ergonômica' : 'Nova ação ergonômica'}
      submitLabel="Guardar ação"
      submitting={submitting}
      error={error}
      onSubmit={submit}
      onClose={onClose}
    >
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            required
            multiline
            minRows={2}
            label="Ação a ser adotada"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            error={Boolean(errors.description)}
            helperText={errors.description || 'Ex.: Adaptar o posto com bancada móvel, evitando o agachamento.'}
            disabled={submitting}
            sx={dialogFieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormControlLabel
            control={<Checkbox checked={done} onChange={(event) => setDone(event.target.checked)} disabled={submitting} />}
            label="Ação já realizada"
            sx={{ minHeight: 44, '& .MuiFormControlLabel-label': { fontSize: '1rem' } }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            type="date"
            label="Prazo"
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
            error={Boolean(errors.dueDate)}
            helperText={errors.dueDate || 'Opcional'}
            disabled={submitting}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={dialogFieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <TextField
            fullWidth
            label="Responsável"
            value={responsible}
            onChange={(event) => setResponsible(event.target.value)}
            error={Boolean(errors.responsible)}
            helperText={errors.responsible || 'Opcional. Ex.: Segurança do trabalho'}
            disabled={submitting}
            sx={dialogFieldSx}
          />
        </Grid>
      </Grid>
    </ItemDialog>
  );
};
